import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, qaPage, speakable, aeoGeo } from "@/lib/seo/schema";

const PATH = "/storytelling-film";
const ANSWER =
  "Storytelling i film betyder att budskapet bärs av en berättelse med en person, ett problem och en förändring i stället för av en lista med argument. Fem modeller täcker de flesta företagsfilmer: hjälteresan, problem–lösning, före–efter, ursprungsberättelsen och vittnesmålet.";

const MODELS = [
  { id: "hjalteresan", name: "1. Hjälteresan", use: "Kundcase-film", body: "Kunden är hjälten, inte ert företag. Ni är vägvisaren som hjälper hjälten över hindret. Strukturen: vardag → problem → möte med er → förändring → nytt läge.", link: ["/kundcase-film", "kundcase-film"] },
  { id: "problem-losning", name: "2. Problem–lösning", use: "Explainer och produktfilm", body: "Visa problemet så konkret att tittaren känner igen sig inom tre sekunder, sedan lösningen i ett enda steg. Fungerar bäst under 90 sekunder.", link: ["/explainer-video", "explainer-video"] },
  { id: "fore-efter", name: "3. Före–efter", use: "Reklamfilm och sociala medier", body: "Kontrasten är berättelsen. Två bilder av samma situation — utan och med er — säger mer än en förklaring. Passar kortformat där varje sekund räknas.", link: ["/reklamfilm", "reklamfilm"] },
  { id: "ursprung", name: "4. Ursprungsberättelsen", use: "Varumärkesfilm och Heritage", body: "Varför finns företaget? Grundarens beslut, tvivel och vändpunkter ger varumärket en röst som konkurrenter inte kan kopiera.", link: ["/heritage", "Heritage-film"] },
  { id: "vittnesmal", name: "5. Vittnesmålet", use: "Employer branding och rekrytering", body: "Medarbetare berättar med egna ord. Intervjun styrs med öppna frågor som ger scener, inte slogans: \"Berätta om en dag då …\".", link: ["/employer-branding-film", "employer branding-film"] },
];

const FAQ: LandingFAQ[] = [
  { q: "Vad är storytelling i film?", a: "Att låta budskapet bäras av en berättelse — en person, ett problem och en förändring — i stället för av påståenden om produkten." },
  { q: "Vilken storytellingmodell passar företagsfilm?", a: "Hjälteresan för kundcase, problem–lösning för explainer och produktfilm, ursprungsberättelsen för varumärkesfilm och vittnesmålet för employer branding." },
  { q: "Fungerar storytelling i korta filmer?", a: "Ja. Före–efter och problem–lösning fungerar ner till 15 sekunder, eftersom kontrasten gör berättelsen utan att den behöver förklaras." },
  { q: "Vad är skillnaden mellan storytelling och manus?", a: "Storytelling är valet av berättelse och struktur. Manuset är den färdiga texten — vad som sägs och visas, scen för scen." },
];

const TITLE = "Storytelling i film — 5 berättarmodeller för företag";
const DESC =
  "Storytelling i film förklarat: fem berättarmodeller (hjälteresan, problem–lösning, före–efter, ursprung, vittnesmål) och vilken filmtyp varje modell passar.";

export const Route = createFileRoute("/storytelling-film")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: PATH, name: TITLE, description: DESC })) },
      { type: "application/ld+json", children: JSON.stringify(qaPage({ question: "Vad är storytelling i film?", answer: ANSWER, path: PATH, authorName: "Fenny" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Manus och koncept", path: "/manus-och-koncept" }, { name: "Storytelling i film", path: PATH }])) },
    ],
  }),
  component: Page,
});

const a = "underline decoration-mint/40 underline-offset-4 hover:text-mint";

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Guide — storytelling"
        title={<>Storytelling i film: 5 berättarmodeller för företag</>}
        lede={ANSWER}
        tldr={<p>Välj modell efter filmtyp: <strong>hjälteresan</strong> för kundcase, <strong>problem–lösning</strong> för explainer, <strong>före–efter</strong> för kortformat, <strong>ursprung</strong> för varumärke och <strong>vittnesmål</strong> för rekrytering.</p>}
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Manus och koncept", to: "/manus-och-koncept" }}
        trackingId="storytelling-film"
        sections={[
          ...MODELS.map((m) => ({
            id: m.id,
            heading: m.name,
            body: (
              <p>
                <strong>Passar: {m.use}.</strong> {m.body} Se hur vi arbetar med{" "}
                <a className={a} href={m.link[0]}>{m.link[1]}</a>.
              </p>
            ),
          })),
          {
            id: "fran-berattelse-till-manus",
            heading: "Från berättelse till manus",
            body: (
              <p>
                När modellen är vald skrivs berättelsen ut som{" "}
                <a className={a} href="/manus-och-koncept">manus</a>, tidsätts mot{" "}
                <a className={a} href="/ord-per-minut">130–150 ord per minut</a> och översätts till{" "}
                <a className={a} href="/storyboard">storyboard</a> innan inspelning.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={3}
        closing={{
          title: "Vilken berättelse bär er film?",
          body: "Vi hjälper er att välja modell och skriva manuset.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "process"]} />
    </>
  );
}
